import { ModalInput } from "components/atoms/input/modal/ModalInput";
import * as s from "pages/maintenance/firmware/FirmwareUpgrade/FirmwareStyled";
import { MdUpdate } from "react-icons/md";
import Toggle from "components/atoms/toggle/Toggle";
import TimeSelect from "components/atoms/select/date/TimeSelect";
import { timeOptions } from "utils/options";
import { FirmwareUpgradeDto } from "api/interfaces/managerInterface.interface";
import CenterContentPage from "components/templates/layout/CenterContentPage";
import Spinner from "components/atoms/loader/Spinner";
import { getTimezoneAbbr } from "utils/timeUtil";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { ButtonWrapper } from "styles/ModalCommonStyled";
import BigButton from "components/atoms/buttons/BigButton";
import { useMenu } from "hook/useMenu";
import { FiInfo } from "react-icons/fi";

type Props = {
  data: FirmwareUpgradeDto;
  onChange: (key: keyof FirmwareUpgradeDto, value: boolean | string) => void;
  onClickSave: () => void;
  disabled: boolean;
  isLoading: boolean;
  firmwareAvailable: {
    CH4: string;
    CH16: string;
  };
};

export default function FwUpgradePresenter({
  data,
  onChange,
  onClickSave,
  disabled,
  isLoading,
  firmwareAvailable,
}: Props) {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { menuNavigation } = useMenu({});
  return (
    <s.FirmwareUpgradeStyled>
      <CenterContentPage
        titleComponent="COVA Firmware Auto-Update"
        titleDescription={
          <s.TitleDescription>
            <p>
              <MdUpdate size={14} /> Turn on “Auto Update” to let myDW
              automatically update when new firmware is released.
            </p>
            <s.InfoWrapper>
              <FiInfo size={14} /> The Firmware Upgrade feature is currently
              available only for COVA.
              <br />
              {firmwareAvailable.CH4 !== firmwareAvailable.CH16 ? (
                <>
                  For 4CH and 8CH models, firmware version{" "}
                  {firmwareAvailable.CH4} or later is required.
                  <br />
                  For 16CH models, firmware version {firmwareAvailable.CH16} or
                  later is required.
                </>
              ) : (
                <>
                  Firmware version {firmwareAvailable.CH4} or later is required.
                </>
              )}
            </s.InfoWrapper>
          </s.TitleDescription>
        }
        buttons={
          <ButtonWrapper>
            <BigButton
              label="Back"
              buttonType="cancel"
              onClickBtn={() => menuNavigation({ to: "/maintenance" })}
              data-testid="maintenance-firmware-firmware-upgrade-button-menu-navigation"
            />

            <BigButton
              label="Save"
              onClickBtn={onClickSave}
              disabled={disabled}
              data-testid="maintenance-firmware-firmware-upgrade-button-click-save"
            />
          </ButtonWrapper>
        }
      >
        {isLoading ? (
          <Spinner />
        ) : (
          <>
            <ModalInput
              label={
                <s.ItemTitle>
                  Auto Update{" "}
                  <small>
                    When enabled, new firmware will be automatically installed
                    during your selected time window.
                  </small>
                </s.ItemTitle>
              }
            >
              <Toggle
                isToggled={data.isEnabled}
                checked={data.isEnabled}
                onChange={(value) => onChange("isEnabled", value)}
                dataTestId="maintenance-firmware-firmware-upgrade-is-enabled-toggle"
              />
            </ModalInput>
            <ModalInput
              label={
                <s.ItemTitle>
                  Update Time{" "}
                  <small>
                    Set a preferred time window for automatic updates.
                  </small>
                </s.ItemTitle>
              }
            >
              <s.TimeSelectWrapper>
                <TimeSelect
                  value={data.updateTime}
                  options={timeOptions}
                  onChange={(value) => onChange("updateTime", value)}
                  disabled={!data.isEnabled}
                  data-testid="maintenance-firmware-firmware-upgrade-time-select-change"
                />

                <span>[ {getTimezoneAbbr(selectedAccount.timezone)} ]</span>
              </s.TimeSelectWrapper>
            </ModalInput>
            <ModalInput
              label={
                <s.ItemTitle>
                  Send Upgrade Notifications{" "}
                  <small>
                    Get notified when new firmware is available. Alerts include
                    device information, version details, release notes, and
                    update options.
                  </small>
                </s.ItemTitle>
              }
            >
              <s.CheckboxItem>
                <input
                  type="checkbox"
                  checked={data.notifyPush}
                  onChange={(e) => onChange("notifyPush", e.target.checked)}
                  id="notifyPush"
                  disabled={!data.isEnabled}
                  data-testid="maintenance-firmware-firmware-upgrade-notify-push-change"
                />{" "}
                <label htmlFor="notifyPush">Push</label>
              </s.CheckboxItem>
              <s.CheckboxItem>
                <input
                  type="checkbox"
                  checked={data.notifyEmail}
                  onChange={(e) => onChange("notifyEmail", e.target.checked)}
                  id="notifyEmail"
                  disabled={!data.isEnabled}
                  data-testid="maintenance-firmware-firmware-upgrade-notify-email-change"
                />{" "}
                <label htmlFor="notifyEmail">Email</label>
              </s.CheckboxItem>
            </ModalInput>
          </>
        )}
      </CenterContentPage>
    </s.FirmwareUpgradeStyled>
  );
}
